import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAudits, createAudit, updateAuditStatus, deleteAudit } from '../../services/auditAPI';
import { setAudits, setLoading, setError } from '../../redux/slices/auditSlice';
import { setTemplates } from '../../redux/slices/templateSlice';
import { getTemplates } from '../../services/templateAPI';
import { Button, Modal, Select, Spin, Card, message, Tag } from 'antd';
import { PlusOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
const { Option } = Select;

const AuditManagementPage = () => {
  const dispatch = useDispatch();
  const { audits, loading, error } = useSelector((state) => state.audit);
  const { templates } = useSelector((state) => state.template);
  const [modalVisible, setModalVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAudits();
    fetchTemplates();
  }, []);

  const fetchAudits = async () => {
    dispatch(setLoading(true));
    try {
      const data = await getAudits();
      // Sort audits by createdAt in descending order to show the most recent first
      const sortedAudits = data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      dispatch(setAudits(sortedAudits));
    } catch (err) {
      dispatch(setError(err.message));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const fetchTemplates = async () => {
    dispatch(setLoading(true));
    try {
      const data = await getTemplates();
      dispatch(setTemplates(data));
    } catch (err) {
      message.error('Failed to fetch templates');
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleTemplateSelect = async (template) => {
    const newAudit = {
      title: template.title,
      description: template.category,
      templateId: template._id,
    };

    try {
      const res = await createAudit(newAudit);
      message.success('Audit created successfully');
      setModalVisible(false);
      fetchAudits(); // Refetch audits to include the new one and update serial numbers
      navigate(`/admin/audit-details/${res._id}`);
    } catch (err) {
      message.error(err.message);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await updateAuditStatus(id, status);
      message.success('Status updated');
      fetchAudits(); // Refetch audits to ensure data is fresh
    } catch (err) {
      message.error(err.message);
    }
  };

  const handleDeleteAudit = async (id) => {
    try {
      await deleteAudit(id);
      message.success('Audit deleted');
      fetchAudits(); // Refetch audits to update the list and serial numbers
    } catch (err) {
      message.error(err.message);
    }
  };

  const statusColorMap = {
    Pending: 'yellow',
    'In Progress': 'blue',
    Completed: 'green',
  };

  return (
    <div className=" min-h-screen">
      <div className="max-w-5xl mx-auto rounded-lg shadow-md p-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800">Audit Management</h2>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)}>
            Assign New Audit
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <Spin size="large" />
          </div>
        ) : error ? (
          <p className="text-red-500">{error}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-md shadow">
              <thead className="bg-gray-100">
                <tr>
                  {/* Added Serial No. header */}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Serial No.</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/* Added index for serial number */}
                {audits.map((audit, index) => (
                  <tr key={audit._id}>
                    {/* Added Serial No. data */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{index + 1}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{audit.title}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{audit.description}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(audit.createdAt).toLocaleDateString() || "N/A"}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {/* You might want to add a Tag component here for status with color */}
                      {audit.status}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Link to={`/admin/audit-details/${audit._id}`} className="mr-2">
                        <Button icon={<EyeOutlined />} size="small">
                          View
                        </Button>
                      </Link>
                      <Button danger icon={<DeleteOutlined />} size="small" onClick={() => handleDeleteAudit(audit._id)}>
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Modal
          title={<h3 className="text-lg font-semibold text-gray-800">Select a Template to Create Audit</h3>}
          visible={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={null}
          width={800}
        >
          {loading ? (
            <div className="flex justify-center py-10">
              <Spin size="large" />
            </div>
          ) : templates.length === 0 ? (
            <p className="text-gray-700 italic mt-4">No templates available. Please create templates first.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
              {templates.map((template) => (
                <Card
                  key={template._id}
                  title={<div className="font-semibold text-gray-700">{template.title}</div>}
                  bordered
                  hoverable
                  onClick={() => handleTemplateSelect(template)}
                  className="shadow-md hover:shadow-lg transition duration-300 cursor-pointer border-gray-200 hover:border-blue-300"
                >
                  <p className="text-gray-600">{template.category}</p>
                </Card>
              ))}
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default AuditManagementPage;
